import { useState } from "react"
import Button from "react-bootstrap/Button"
import { useLocation } from "react-router"
import "./index.scss"
import { ConfirmDialog } from "components"
import { Container, Text, Form } from "components/Form"
import { FiInfo } from "react-icons/fi"
import useT from "utils/hooks/useT"

// The main configuration container.
// Used to add a title and buttons at the bottom of a configuration.
// A lot of props can be passed for different scenarios. They are
// pretty self-explanatory
export default function ConfigContainer({
  title,
  titleInfoText,
  disabled,
  cancelHandler,
  canCancelWhenDisabled,
  submitHandler,
  deleteIsImportant,
  keyWordForAllowedDeletion,
  deleteHandler,
  shiftDeleteHandler,
  deleteTitle,
  // SPEC-045 (K3/K4/K11): when the delete/unplan action is blocked (e.g. a hard-locked analysis),
  // the caller passes deleteDisabled to render the delete button ghosted (grey, inactive, with a
  // not-allowed cursor + tooltip) instead of active — rather than hiding it. Backward-compatible:
  // both default off, so every other caller is unchanged.
  deleteDisabled = false,
  deleteDisabledTitle,
  confirmDeletionText,
  canDeleteWhenDisabled,
  saveTemplateHandler,
  saveAsHandler,
  saveDraftHandler,
  saveAsName,
  canSaveWhenDisabled,
  closeInsteadOfCancel,
  children,
  isDraft,
  isTemplate,
  className,
  editProjectHandler,
}) {
  const { t } = useT("common")
  const [activeConfirm, setActiveConfirm] = useState(false)
  const [allowDeletionKeyWord, setAllowDeletionKeyWord] = useState("")
  const [isWorking, setIsWorking] = useState(false)

  const shiftClickDeletionTexts = ["Are you sure you want to unplan all future actions in this project?"]
  const location = useLocation()
  const isAnalysisView = location.pathname.includes("/analyses/")

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault() // Prevent default form submission behavior

    if (activeConfirm) {
      // If delete confirmation is active, do not proceed with submitHandler
      return
    }

    const returnValue = submitHandler()
    if (returnValue && title === "Analysis Definition") {
      setIsWorking(true)
      Promise.resolve(returnValue).finally(() => setIsWorking(false))
    }
  }

  // Handle deletion confirmation
  const handleConfirmDeletion = () => {
    if (deleteHandler) {
      deleteHandler(allowDeletionKeyWord)
    }
    setActiveConfirm(false)
    setAllowDeletionKeyWord("")
  }

  return (
    <Form
      className="ConfigContainer"
      onSubmit={handleSubmit}
    >
      {title && (
        <div className={titleInfoText ? "setupHeading ConfigContainer-Title" : "setupHeading"}>
          {title}
          {titleInfoText && (
            <div className="connection-info">
              <FiInfo title={titleInfoText} />
            </div>
          )}
        </div>
      )}
      <div className={`Config-main-container HiddenInput-container ${className}`}>
        {children}
        {deleteHandler && keyWordForAllowedDeletion && activeConfirm && (
          <ConfirmDialog
            onCancel={() => setActiveConfirm(false)}
            onConfirm={handleConfirmDeletion}
          >
            <h4>{t("toConfirmDeletionType", { keyword: keyWordForAllowedDeletion })}</h4>
            <Container>
              <Text
                value={allowDeletionKeyWord}
                onChange={(e) => setAllowDeletionKeyWord(e)}
                // Prevent Enter key from submitting the form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
              />
            </Container>
          </ConfirmDialog>
        )}

        {deleteHandler && activeConfirm && !keyWordForAllowedDeletion && (
          <ConfirmDialog
            onCancel={() => setActiveConfirm(false)}
            onConfirm={() => {
              if (shiftDeleteHandler) {
                shiftDeleteHandler()
              } else {
                deleteHandler()
              }
              setActiveConfirm(false)
            }}
          >
            <h4>{confirmDeletionText || t("confirmDeletion")}</h4>
          </ConfirmDialog>
        )}
      </div>
      <div className="ConfigContainer-buttons">
        {(canDeleteWhenDisabled || !disabled) && deleteHandler && (
          deleteDisabled ? (
            // Ghosted: the wrapper span carries the not-allowed cursor + tooltip (a disabled
            // <button> has pointer-events:none and would show neither). SPEC-045 K11.
            <span className="blocked-action-wrap" title={deleteDisabledTitle}>
              <Button variant="secondary" className="blocked-action-btn" disabled>
                {deleteTitle || t("delete")}
              </Button>
            </span>
          ) : (
            <Button
              variant="danger"
              onClick={(e) => {
                const shiftKeyEffect = e.shiftKey && shiftClickDeletionTexts.includes(confirmDeletionText)
                if (deleteIsImportant || keyWordForAllowedDeletion || shiftKeyEffect) {
                  setActiveConfirm(true)
                } else {
                  deleteHandler()
                }
              }}
            >
              {deleteTitle || t("delete")}
            </Button>
          )
        )}
        {(canCancelWhenDisabled || !disabled) && (
          <Button
            variant="light"
            onClick={() => {
              cancelHandler()
            }}
          >
            {closeInsteadOfCancel ? t("close") : t("cancel")}
          </Button>
        )}
        {editProjectHandler && (
          <Button variant="info" style={{color: "white"}} onClick={editProjectHandler}>
            {t("editProject")}
          </Button>
        )}
        {!disabled && saveTemplateHandler && (
          <Button variant="warning" onClick={() => saveTemplateHandler()}>
            {t("saveAsNewTemplate")}
          </Button>
        )}
        {!disabled && saveAsHandler && !isDraft &&  (
          <Button variant="warning" onClick={() => saveAsHandler()}>
            {saveAsName || t("saveAsNew")}
          </Button>
        )}
        {!disabled && isDraft && saveDraftHandler && (
          <Button variant="warning" onClick={() => saveDraftHandler()}>
            {t("saveAsDraft", { type: isAnalysisView ? "" : t("projectTemplate") })}
          </Button>
        )}
        {(canSaveWhenDisabled || !disabled) && (
          <Button variant="primary" type="submit" disabled={isWorking}>
            {isDraft ? t("saveAsType", { type: isTemplate ? t("projectTemplate") : t("analysisDefinition") }) : t("save")}
          </Button>
        )}
      </div>
    </Form>
  )
}

